import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, TextField } from "@mui/material";
import { commonBoxStyle, MainLayout } from "../layout/mainLayout";
import { PageTitle } from "../layout/pageTitle";
import { useTranslation } from "react-i18next";
import { LoadingButton } from "../framework/loadingButton";
import { useAppDispatch, useAppSelector } from "../../framework/redux/hooks";
import { handleApiError, IApiError } from "../../services/baseService";
import { saveStartWorkTime } from "../../framework/redux/slices/workShiftTimeSlotSlice";
import { saveWorkTimeLocation, WorkShiftTimeSlotStartResult } from "../../services/workShiftTimeSlotService";
import { getGeolocationPosition } from "../../framework/common/geolocation";
import { AlertSeverity } from "../../models/common/enums";
import { workTimeGeolocationPositionErrorDuration } from "../worktime/workTimeTypeDrawer";
import { useSelectCurrentWorkShiftTimeSlot } from "../../hooks/worktime/useSelectWorkShiftTimeSlots";
import { useWorkTimeDefaultTimezone } from "../../hooks/framework/useWorkTimeDefaultTimezone";
import { addAlert } from "../../framework/redux/slices/appSlice";

export const WorkTimeTypeCommentForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const current = useSelectCurrentWorkShiftTimeSlot();
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);
    const { workTimeTypeId } = useParams();
    const [workStageComment, setWorkStageComment] = useState<string>("");
    const [defaultTz] = useWorkTimeDefaultTimezone();
    const isEmptyText = /^(?!\s+$).*/;

    const usesLocationData = useAppSelector(
        ({ session }) => session.employeeGroupParameters?.usesLocationData
    );
    const formItem = useAppSelector(
        ({ workShiftTimeSlot }) => workShiftTimeSlot.formItem
    );

    useEffect(() => {
        if (!workStageComment || !isEmptyText.test(workStageComment)) {
            setIsSaveDisabled(true);
        } else {
            setIsSaveDisabled(false);
        }
    }, [workStageComment]);

    const handleSubmit = async () => {
        setIsSaving(true);

        const { costCenterId, vehicleId, salaryRowTypeId, customerId } =
            formItem || current || {};

        await dispatch(
            saveStartWorkTime({
                costCenterId,
                workTimeTypeId,
                timezoneName: defaultTz,
                vehicleId,
                salaryRowTypeId,
                customerId,
                comment: workStageComment,
            })
        )
            .unwrap()
            .then((result: WorkShiftTimeSlotStartResult) => {
                if (usesLocationData) {
                    getGeolocationPosition({
                        message: t("workTime.geolocationPositionError"),
                        severity: AlertSeverity.Warning,
                        duration: workTimeGeolocationPositionErrorDuration,
                    })
                        .then((location) => {
                            if (!!location)
                                return Promise.all([
                                    saveWorkTimeLocation(
                                        result.newSlot.id,
                                        location,
                                        "start"
                                    ),
                                    result.finishedSlotId
                                        ? saveWorkTimeLocation(
                                            result.finishedSlotId,
                                            location,
                                            "finish"
                                        )
                                        : Promise.resolve(),
                                ]);
                        })
                        .catch(console.error);
                }
                navigate("/");
            })
            .catch((e: IApiError) => {
                if (e?.code === "409") {
                    dispatch(
                        addAlert({
                            severity: AlertSeverity.Warning,
                            message: t("workTime.workTimeStartConflict"),
                            duration: 5000,
                        })
                    );
                } else {
                    handleApiError(e);
                }
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    return (
        <MainLayout
            hasPx
            topComponent={
                <PageTitle
                    header={`${t("workTime.addDefinition")}`}
                />
            }
        >
            {workTimeTypeId ? (
                <>
                    <Box sx={commonBoxStyle} mb={2}>
                        <TextField
                            name="comment"
                            label={t("common.comment")}
                            multiline
                            minRows={2}
                            fullWidth
                            value={workStageComment}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setWorkStageComment(event.target.value);
                            }}
                        />
                    </Box>
                    <Box sx={commonBoxStyle}>
                        <LoadingButton
                            onClick={() => void handleSubmit()}
                            variant="contained"
                            color="info"
                            disabled={isSaving}
                            loading={isSaving && !workStageComment}
                        >
                            {t("workTime.continueWithoutDefinition")}
                        </LoadingButton>
                        <LoadingButton
                            onClick={() => void handleSubmit()}
                            variant="contained"
                            color="success"
                            disabled={isSaveDisabled || isSaving}
                            loading={isSaving && workStageComment?.length > 0}
                        >
                            {t("common.save")}
                        </LoadingButton>
                    </Box>
                </>
            ) : (
                <Box sx={commonBoxStyle}>{t("workTime.workTimeTypeMissing")}</Box>
            )}
        </MainLayout>
    );
};
